import * as React from 'react';
import { cn } from '@/utils/utils';
import { CaretDownIcon } from '@phosphor-icons/react';
import { CardTitle } from '@/components/ui/text';
import { IconButton } from '@/components/ui/icon-button';

/** The card's inner spacing: none for a card its content fills (an image), sm for a dense card, md for most, lg for a page's main card. */
const PADDINGS = { none: '', sm: 'p-4', md: 'p-5 sm:p-6', lg: 'p-6 sm:p-8' } as const;

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: keyof typeof PADDINGS;
  /** A card that does something when clicked: it lifts on hover, its line in the brand ink. */
  interactive?: boolean;
  /** A stage framing a preview beside its controls (a 3D view, a cropper, a trimmer): no shadow, level with its panel. */
  flat?: boolean;
  /** high: a card standing further off the page (a project, template or product tile, an account, settings, analytics or activity section): a larger, darker drop instead of the raised glow. */
  elevation?: 'raised' | 'high';
}

/** Lureo's card: a surface in the page's flow (a project, a form section, a summary). */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(({ padding = 'md', interactive = false, flat = false, elevation = 'raised', className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="card"
    className={cn(
      'rounded-lureo-radius-lg border-lureo-border-width line-style-lureo-line-style bg-lureo-surface text-lureo-ink-strong',
      // On a page or in a card it is raised, or high, its shadow drawing its edge as the theme turns to glass; flat, or in a dialog or panel, its line draws it
      flat
        ? 'border-lureo-line'
        : cn(
            'border-lureo-card-line in-[[role=dialog]]:border-lureo-line in-[[role=dialog]]:shadow-none in-[[role=alertdialog]]:border-lureo-line in-[[role=alertdialog]]:shadow-none in-[[data-slot=editor-panel]]:border-lureo-line in-[[data-slot=editor-panel]]:shadow-lureo-shadow-panel-card',
            elevation === 'high' ? 'shadow-lureo-shadow-modal' : 'shadow-lureo-shadow-raised',
          ),
      PADDINGS[padding],
      // A card that does something lifts as the pointer comes over it, its line and shadow its hover ones (not in a dialog or panel, which draw no shadow)
      interactive && 'cursor-pointer transition-all duration-lureo-duration-slow ease-lureo-easing-standard hover:scale-lureo-card-hover-scale hover:border-lureo-card-hover-line hover:bg-lureo-card-hover',
      interactive && !flat && elevation === 'raised' && 'hover:shadow-lureo-shadow-card-hover in-[[role=dialog]]:hover:shadow-none in-[[role=alertdialog]]:hover:shadow-none in-[[data-slot=editor-panel]]:hover:shadow-lureo-shadow-panel-card',
      // A library's tile in an editor panel sits flat with smaller corners and doesn't lift, as the editor always drew it; the panel's large stages keep theirs
      interactive && 'in-[[data-slot=editor-panel]]:rounded-lureo-radius-sm in-[[data-slot=editor-panel]]:hover:scale-100',
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

interface CardHeadingProps {
  /** The section's icon, drawn in the accent ink at h-5. */
  icon?: React.ReactNode;
  title: React.ReactNode;
  /** A line under the title: what the card is for. */
  description?: React.ReactNode;
  /** What sits at the heading's end: an action, a count. */
  aside?: React.ReactNode;
  /** The heading level the page's outline needs. */
  as?: 'h2' | 'h3' | 'h4';
  /** A card whose body folds away: whether it is open, and the caret at the heading's end that folds it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/** A card's heading: its icon, its title and what sits at its end. */
export function CardHeading({ icon, title, description, aside, as = 'h3', open, onOpenChange, className }: CardHeadingProps) {
  const fold = onOpenChange && open !== undefined && (
    <IconButton label={typeof title === 'string' ? title : 'Show or hide'} aria-expanded={open} onClick={() => onOpenChange(!open)}>
      <CaretDownIcon className={cn('h-4 w-4 transition-transform', !open && '-rotate-90')} />
    </IconButton>
  );
  return (
    <div className={cn('flex gap-2', open !== false && 'mb-4', description ? 'items-start' : 'items-center', className)}>
      {icon && <span aria-hidden className={cn('inline-flex shrink-0 text-lureo-sky-ink [&_svg]:h-5 [&_svg]:w-5', description && 'mt-1')}>{icon}</span>}
      <div className="flex min-w-0 flex-1 flex-col">
        <CardTitle as={as} className={cn('truncate', description && 'leading-7')}>{title}</CardTitle>
        {description && <p className="font-lureo-font-ui text-lureo-14 leading-5 text-lureo-ink-subtle">{description}</p>}
      </div>
      {(aside || fold) && (
        <div className="flex shrink-0 items-center gap-2">
          {aside}
          {fold}
        </div>
      )}
    </div>
  );
}
